import type { Cycle } from "../components/frozen-storage-data";
import { buildPrefillCycle, type SpecimenPrefill } from "./specimenIntakeForm";

/**
 * Making the treatment hub's numbers and the vault's inventory the same event.
 *
 * They were two stores with nothing between them. The hub's freezing step wrote
 * a phase and its `fertilityMetrics` — numbers — while the only thing in the app
 * that can create a specimen row is `AddSpecimenModal`. So recording "8 frozen"
 * in the hub produced, without fail, a cycle whose recap said "8 frozen, 0
 * attached" and an orange banner asking her to enter it a second time by hand.
 *
 * The banner was not wrong. It reported a real gap — and it fired on the
 * ordinary path, for everyone, which is what turned a warning into noise.
 *
 * This closes the gap at the source: recording the count writes the rows. It
 * reuses the intake's own machinery rather than a second way of writing
 * specimens, so a re-save updates the rows it already made, a larger count adds
 * to them, and a smaller one retires the tail — the same rules, from the same
 * code, whichever surface she used.
 */

/** Which step field carries the frozen count, per preservation cycle. */
export const FROZEN_COUNT_SOURCE = {
  egg_freezing: { step: "freezing", field: "eggsFrozen", type: "eggs" },
  /*
   * `embryo_development`, not `fertilization`. That step is inserted into the
   * embryo-freezing flow at runtime (`insertRequiredStep`, `treatmentFlows.ts`)
   * and it is the only one that carries `embryosFrozen` — `usePhases` maps
   * `frozen_embryo` back to that field for that step id alone. Pointed at
   * `fertilization`, this synced nothing at all for embryo cycles: the step
   * that fires has the wrong id, and the step with the right id has no field.
   */
  embryo_freezing: {
    step: "embryo_development",
    field: "embryosFrozen",
    type: "embryos",
  },
  sperm_freezing: { step: "freezing", field: "vialsFrozen", type: "sperm" },
} as const;

export type PreservationCycleType = keyof typeof FROZEN_COUNT_SOURCE;

/**
 * The most rows one unattended save may create.
 *
 * Not a clinical limit — cohorts this large exist — but a typo guard. Nothing
 * on this path can undo the result: once a cycle holds rows the sync refuses to
 * run again, so a slipped digit would stand until she retired the surplus by
 * hand. Above this the count is left to the intake, where she sees what she is
 * creating.
 */
export const MAX_SYNCED_SPECIMENS = 60;

export function isPreservationCycleType(
  cycleType: string | null | undefined,
): cycleType is PreservationCycleType {
  return cycleType != null && cycleType in FROZEN_COUNT_SOURCE;
}

/**
 * The cycle to hand `useCreateSpecimen`, or null when there is nothing honest
 * to write.
 */
export function buildInventorySyncCycle({
  cycleType,
  frozenCount,
  prefill,
  country,
  recordedDate,
}: {
  cycleType: PreservationCycleType;
  /** What she has just recorded on the step that owns the count. */
  frozenCount: number | null | undefined;
  /** What the intake already knows: her saved rows, dates and storage block. */
  prefill: SpecimenPrefill;
  /**
   * Her own country, so a first sync does not stamp every row `US`.
   * Required: `buildPrefillCycle` defaults to "US" when handed nothing, which
   * is the very outcome this exists to prevent.
   */
  country: string;
  /**
   * A date from the save itself, for the first sync — the prefill is a memo
   * from before it, so a step that supplies the count and the date together
   * (semen does) would otherwise find no date and write nothing.
   */
  recordedDate?: string;
}): Cycle | null {
  if (frozenCount == null) return null;

  /*
   * It creates the first inventory, and only that.
   *
   * Once a cycle holds rows, they are the record: what diverges from the step's
   * count afterwards is a thaw, a use, a correction — history the step data
   * does not track and cannot reconstruct. Re-running the count over them would
   * write back rows she has since consumed. So growing, correcting and retiring
   * a cohort all stay in the intake, where she can see the rows she is changing;
   * this only makes sure she is never left with none.
   */
  if (prefill.savedRowTotal) return null;

  /*
   * Zero is left alone, deliberately.
   *
   * It would mean retiring the whole cohort, and a step form is not where a
   * patient expects her vault to be emptied. `surplusClosures` refuses a count
   * of zero for the same reason. She can still take a cohort back through the
   * intake, where the number she is changing is the one in front of her.
   */
  if (frozenCount <= 0) return null;

  const type = FROZEN_COUNT_SOURCE[cycleType].type;
  const cycle = buildPrefillCycle({ ...prefill, type }, country);
  /*
   * The date from this save wins over the one the memo carries.
   *
   * Not a fallback. For embryos the prefill's freeze date is the FERTILISATION
   * phase's start — one step earlier than the event it would be naming — so a
   * freeze date she typed on `embryo_development` was being discarded and every
   * row stamped with the fertilisation day instead.
   */
  if (recordedDate) {
    if (type === "eggs") cycle.oocyte.freezeDate = recordedDate;
    else if (type === "sperm") cycle.sperm.freezeDate = recordedDate;
    else cycle.embryo.freezeDate = recordedDate;
  }

  /*
   * A date, or nothing at all. `useCreateSpecimen` builds the storage placement
   * only from `freezeDate || retrievalDate`, and a row written without one
   * lands as `processed` — which the vault does not show. Creating rows she
   * cannot see would replace a visible warning with an invisible one.
   */
  const hasDate =
    type === "eggs"
      ? cycle.oocyte.freezeDate || cycle.oocyte.retrievalDate
      : type === "sperm"
        ? cycle.sperm.freezeDate || cycle.sperm.collectedAt
        : cycle.embryo.freezeDate || cycle.embryo.fertilisationDate;
  if (!hasDate) return null;

  if (type === "eggs") cycle.oocyte.numberFrozen = frozenCount;
  else if (type === "sperm") cycle.sperm.numberFrozen = frozenCount;
  else cycle.embryo.numberFrozen = frozenCount;

  /*
   * Recording that they are frozen IS the claim that they are stored.
   *
   * The two are the same fact in this model, and the vault reads only `stored`
   * rows with an active placement — so without this the rows land `processed`,
   * invisible, and the banner goes on saying "0 attached" about material she
   * has just recorded.
   *
   * "unknown" does not block it. The picker calls it "Inconnu / à confirmer",
   * which is a question still open rather than an answer — and it is what the
   * intake leaves behind when she did not know WHERE, a case #3533 made
   * recordable on purpose. Reading it as a refusal is what made this whole sync
   * a no-op: `if (!cycle.storageState)` treats the string as an answer, so the
   * guard never fired and eight rows were written with no placement at all.
   *
   * `not_stored` is the one real negation, and it is honoured. She has said
   * these are not in storage; a count does not overrule her.
   */
  if (cycle.storageState !== "not_stored") cycle.storageState = "stored";

  return cycle;
}
